import Flickity from 'flickity'
import 'flickity/dist/flickity.min.css'

/**
 * CUSTOM ELEMENT
 * Flickity Element
 *
 * Using the Flickity instance under the hood this element allows us to add additional features such as
 * Controller buttons (top right)
 * Progress Bar
 *
 * For a working example of the x-flickity search for the 'article-grid.liquid' file.
 */
class FlickityElement extends HTMLElement {
  static NEXT_SELECTOR = '[data-role="next"]'
  static PREVIOUS_SELECTOR = '[data-role="previous"]'
  static PROGRESS_BAR_SELECTOR = '[data-role="progress-bar"]'
  static CONTROLLER_SELECTOR = '[data-role="controller"]'
  static CONTAINER_SELECTOR = '[data-role="container"]'
  static RATING_READY_EVENT = 'klaviyo-star-rating:ready'
  static RATING_FALLBACK_MS = 600
  static RATING_OBSERVER_MS = 10000

  constructor() {
    super()
    this.flickity = null
    this._reloadFrame = null
    this._resizeFrame = null
    this._measuring = false
    this._ratingFallback = null
    this._ratingCleanup = null

    this.handleUpdateCarousel = this.handleUpdateCarousel.bind(this)
    this.handleUpdateProgressBar = this.handleUpdateProgressBar.bind(this)
    this.handleRatingsReady = this.handleRatingsReady.bind(this)

    this.initialiseFlickity = () => {
      if (!this.containerElement) this.fetchChildren()
      if (!this.containerElement) return

      this.teardownObservers()
      if (this.flickity) {
        this.flickity.destroy()
        this.flickity = null
      }

      this.flickity = new Flickity(this.containerElement, {
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: 1,
        pageDots: false,
        imagesLoaded: true,
        on: {
          change: this.handleUpdateProgressBar,
          ready: this.handleUpdateProgressBar
        }
      })

      this.observeSlider()
      this.observeSize()
      this.observeRatings()
      this.handleUpdateProgressBar()
    }

    this.syncFlickity = () => {
      this.scheduleReload()
    }

    this.calculateProgress = () => {
      if (!this.flickity) return 25

      const selectedIndex = this.flickity.selectedIndex
      const groupCells = this.flickity.options.groupCells

      // Flickity thinks there's two more elements than there actually are
      const totalCells = Math.max(0, this.flickity.cells.length)
      const cellCount = Math.max(0, groupCells * Math.round(totalCells / groupCells) - 1)
      const visibleCells = Math.min(selectedIndex * groupCells, cellCount)

      const actualProgress = visibleCells / cellCount
      return 25 + 75 * actualProgress
    }
  }

  runWithNaturalCellHeights(callback) {
    if (this._measuring) {
      callback()
      return
    }

    const slider = this.flickity?.slider
    const minHeightElements = slider ? [...slider.querySelectorAll('.min-h-full')] : []
    this._measuring = true
    minHeightElements.forEach((element) => element.classList.remove('min-h-full'))

    try {
      callback()
    } finally {
      minHeightElements.forEach((element) => element.classList.add('min-h-full'))
      this._measuring = false
    }
  }

  scheduleReload() {
    if (this._reloadFrame) return
    this._reloadFrame = requestAnimationFrame(() => {
      this._reloadFrame = null
      if (!this.flickity) {
        this.initialiseFlickity()
        return
      }
      this.runWithNaturalCellHeights(() => {
        this.flickity.reloadCells()
        this.flickity.resize()
      })
      this.handleUpdateProgressBar()
    })
  }

  scheduleResize() {
    if (this._resizeFrame || this._measuring) return
    this._resizeFrame = requestAnimationFrame(() => {
      this._resizeFrame = null
      this.runWithNaturalCellHeights(() => {
        this.flickity?.resize()
      })
      this.handleUpdateProgressBar()
    })
  }

  observeSlider() {
    this.mutationObserver?.disconnect()
    const slider = this.containerElement?.querySelector('.flickity-slider')
    if (!slider) return

    this.mutationObserver = new MutationObserver(() => this.scheduleReload())
    this.mutationObserver.observe(slider, { childList: true })
  }

  observeSize() {
    this.resizeObserver?.disconnect()
    if (!this.containerElement || typeof ResizeObserver === 'undefined') return

    this.resizeObserver = new ResizeObserver(() => this.scheduleResize())
    this.resizeObserver.observe(this.containerElement)
  }

  observeRatings() {
    this.teardownRatings()
    if (!this.containerElement) return

    this.containerElement.addEventListener(FlickityElement.RATING_READY_EVENT, this.handleRatingsReady)
    this._ratingFallback = setTimeout(() => this.scheduleReload(), FlickityElement.RATING_FALLBACK_MS)
    this._ratingCleanup = setTimeout(() => this.teardownRatings(), FlickityElement.RATING_OBSERVER_MS)
  }

  handleRatingsReady() {
    this.scheduleReload()
  }

  teardownRatings() {
    this.containerElement?.removeEventListener(FlickityElement.RATING_READY_EVENT, this.handleRatingsReady)
    if (this._ratingFallback) clearTimeout(this._ratingFallback)
    if (this._ratingCleanup) clearTimeout(this._ratingCleanup)
    this._ratingFallback = null
    this._ratingCleanup = null
  }

  teardownObservers() {
    this.teardownRatings()
    this.mutationObserver?.disconnect()
    this.resizeObserver?.disconnect()
    this.mutationObserver = null
    this.resizeObserver = null
    if (this._reloadFrame) cancelAnimationFrame(this._reloadFrame)
    if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame)
    this._reloadFrame = null
    this._resizeFrame = null
  }

  handleDisableButtons(progress) {
    if (!this.controllerElement) return

    if (progress <= 0) {
      this.previousElement?.setAttribute('disabled', '')
    } else if (progress >= 100) {
      this.nextElement?.setAttribute('disabled', '')
    } else {
      this.previousElement?.removeAttribute('disabled')
      this.nextElement?.removeAttribute('disabled')
    }
  }

  handleUpdateProgressBar() {
    if (!this.progressBarElement) return
    const progress = this.calculateProgress() || 25
    this.progressBarElement.style.setProperty('--progress', progress + '%')
    this.handleDisableButtons(progress)
  }

  handleUpdateCarousel(e) {
    if (!this.flickity) return
    const { role } = e.currentTarget.dataset
    switch (role) {
      case 'previous':
        this.flickity.previous()
        break
      case 'next':
        this.flickity.next()
        break
    }
  }

  fetchChildren() {
    this.containerElement = this.querySelector(FlickityElement.CONTAINER_SELECTOR)
    this.controllerElement = this.querySelector(FlickityElement.CONTROLLER_SELECTOR)
    this.progressBarElement = this.querySelector(FlickityElement.PROGRESS_BAR_SELECTOR)

    if (!this.controllerElement) return
    this.nextElement = this.controllerElement.querySelector(FlickityElement.NEXT_SELECTOR)
    this.previousElement = this.controllerElement.querySelector(FlickityElement.PREVIOUS_SELECTOR)
  }

  addEventListeners() {
    this.nextElement?.addEventListener('click', this.handleUpdateCarousel)
    this.previousElement?.addEventListener('click', this.handleUpdateCarousel)
  }

  removeEventListeners() {
    this.teardownObservers()
    this.flickity?.destroy()
    this.flickity = null
    this.nextElement?.removeEventListener('click', this.handleUpdateCarousel)
    this.previousElement?.removeEventListener('click', this.handleUpdateCarousel)
  }

  connectedCallback() {
    this.fetchChildren()
    this.initialiseFlickity()
    this.addEventListeners()
    this.handleUpdateProgressBar()
  }

  disconnectedCallback() {
    this.removeEventListeners()
  }
}

customElements.define('x-flickity', FlickityElement)
